import { computed } from 'vue'
import { useCommonStore } from '@/stores/CommonStore'
import type { FeatureFlagName } from '@/constants/featureFlags'
import { DateTime } from 'luxon'

/**
 * 🚀 SMART Feature Flags Composable - Value-Based Access Control
 *
 * The feature flag VALUE determines who sees the feature!
 *
 * Usage:
 * import { FEATURE_FLAGS } from '@/constants/featureFlags'
 * const isEnabled = useFeatureFlag(FEATURE_FLAGS.CALENDAR_3_COLUMN)
 *
 * Firebase Remote Config Examples:
 * web_new_feature: false          → Nobody sees it
 * web_new_feature: true           → Everyone sees it
 * web_new_feature: "qa"           → Only QA users see it
 * web_new_feature: "premium"      → Premium+ subscribers see it
 * web_new_feature: "beta,qa"      → Users with 'beta' tag OR QA users see it
 * web_new_feature: "premium,beta" → Premium users OR 'beta' tag users see it
 *
 * No more separate _enabled, _minSubscription, _allowedUserTags parameters!
 */

/**
 * Single feature flag hook - most common usage
 * @param flagName - Name of the feature flag (use FEATURE_FLAGS constants)
 * @returns Computed boolean for whether flag is enabled for current user
 */
export function useFeatureFlag(flagName: FeatureFlagName | string) {
  return computed(() => {
    // Get the feature flag value - this determines access level!
    const flagValue = getRemoteConfigValue(flagName, false)

    return evaluateFlag(flagValue)
  })
}

/**
 * Multiple feature flags hook - for when you need several flags
 * @param flagNames - Array of feature flag names (use FEATURE_FLAGS constants)
 * @returns Object with computed boolean for each flag
 */
export function useFeatureFlags(flagNames: (FeatureFlagName | string)[]) {
  const flags: Record<string, ReturnType<typeof useFeatureFlag>> = {}

  flagNames.forEach((name) => {
    flags[name] = useFeatureFlag(name)
  })

  return flags
}

/**
 * Read already-resolved boolean flags from CommonStore only.
 * No remote-config evaluation here.
 */
export function useStoreFeatureFlag(
  flagName: FeatureFlagName | string,
  defaultValue = false
) {
  const commonStore = useCommonStore()

  return computed<boolean>(() => {
    const flags = commonStore.featureFlags as
      | Record<string, unknown>
      | undefined
    const raw = flags?.[flagName]
    return typeof raw === 'boolean' ? raw : defaultValue
  })
}

/**
 * Get a value from Firebase Remote Config through the CommonStore
 * Now works with ANY flag name dynamically!
 */
function getRemoteConfigValue(key: string, defaultValue: unknown): unknown {
  const commonStore = useCommonStore()

  // Get from the simplified remoteConfigFlags (contains ALL Remote Config values)
  const remoteConfigFlags = commonStore.getRemoteConfigFlags
  if (remoteConfigFlags && key in remoteConfigFlags) {
    // Use dynamic access since we support ANY flag name now
    return (remoteConfigFlags as unknown as Record<string, unknown>)[key]
  }

  // Fall back to legacy featureFlags for backward compatibility
  const legacyFlags = commonStore.featureFlags
  if (legacyFlags && key in legacyFlags) {
    return (legacyFlags as unknown as Record<string, unknown>)[key]
  }

  return defaultValue
} /**
 * Smart feature flag evaluation using the flag VALUE to determine access
 *
 * Supported values:
 * - false/"false"/"off": Feature disabled for everyone
 * - true/"true"/"on": Feature enabled for everyone
 * - "qa": Only QA users see the feature
 * - "premium": Premium+ subscribers see the feature
 * - "standard": Standard+ subscribers see the feature
 * - "beta,qa": Users with beta OR qa tags see the feature
 * - "premium,beta": Premium users OR users with beta tag
 */
function evaluateFlag(flagValue: unknown): boolean {
  // Convert to string for easier processing
  const value = String(flagValue).toLowerCase().trim()

  // 1. Disabled states
  if (
    value === 'false' ||
    value === 'off' ||
    value === '' ||
    flagValue === false
  ) {
    return false
  }

  // 2. Enabled for everyone
  if (value === 'true' || value === 'on' || flagValue === true) {
    return true
  }

  // 3. Check if user meets the specified requirement
  return userMeetsRequirement(value)
}

/**
 * Check if user meets the requirement specified in the flag value
 * Supports: subscription levels, tags, or combinations (comma-separated)
 */
function userMeetsRequirement(requirement: string): boolean {
  const commonStore = useCommonStore()
  const userProps = commonStore.getUserTags
  if (!userProps) return false

  // Handle comma-separated or semi-colon separated requirements (OR logic)
  const requirements = requirement
    .split(/[,;]/)
    .map((r) => r.trim().toLowerCase())

  return requirements.some((req) => {
    // Check if it's a subscription level
    //TODO: rework this, because the numbers and tiers no longer work with highestTier
    // if (isSubscriptionLevel(req)) {
    //   return userIsAtLeastSubscription(req)
    // }
    // Check if user has the tag
    return userHasTag(req)
  })
}

/**
 * Check if a string is a valid subscription level
 */
// function isSubscriptionLevel(level: string): boolean {
//   return ['free', 'standard', 'basic', 'premium'].includes(level)
// }

/**
 * Check if user has a specific tag
 */
function userHasTag(tag: string): boolean {
  const commonStore = useCommonStore()
  const userProps = commonStore.getUserTags
  if (!userProps) return false

  const userTags = userProps.split(/[,;]/).map((t) => t.trim().toLowerCase())
  return userTags.includes(tag.toLowerCase())
}

/**
 * Check if user meets minimum subscription requirement
 */
// function userIsAtLeastSubscription(requiredLevel: string): boolean {
//   const commonStore = useCommonStore()
//   const userProps = commonStore.getUserFeatureProperties
//   if (!userProps) return false

//   const subscriptionHierarchy = ['free', 'standard', 'basic', 'premium']
//   const userIndex = subscriptionHierarchy.indexOf(
//     userProps.subscriptionLevel.toLowerCase()
//   )
//   const requiredIndex = subscriptionHierarchy.indexOf(
//     requiredLevel.toLowerCase()
//   )

//   return userIndex >= requiredIndex
// }

/**
 * Resolve all feature flags by combining defaults, remote config, and user-specific logic
 * This is the central function for feature flag computation
 */
export function resolveAllFeatureFlags(
  defaultFlags: Record<string, boolean | string>,
  remoteConfigFlags?: Record<string, unknown> | null,
  userTags?: string | undefined | null
): Record<string, boolean> {
  const resolved: Record<string, boolean> = {}

  // Start with defaults, using the same evaluation logic as individual flags
  Object.entries(defaultFlags).forEach(([key, value]) => {
    resolved[key] = evaluateFlagValue(value, userTags)
  })

  // Override with remote config if available
  if (remoteConfigFlags) {
    Object.entries(remoteConfigFlags).forEach(([key, value]) => {
      if (key in resolved) {
        resolved[key] = evaluateFlagValue(value, userTags)
      }
    })
  }

  return resolved
}

/**
 * Internal helper to evaluate a flag value with user context
 * Similar to evaluateFlag but can work without CommonStore
 */
function evaluateFlagValue(
  flagValue: unknown,
  userTags?: string | null
): boolean {
  const raw = String(flagValue).trim()
  const value = raw.toLowerCase()

  // 1. Disabled
  if (
    value === 'false' ||
    value === 'off' ||
    value === '' ||
    flagValue === false
  ) {
    return false
  }

  // 2. Enabled for everyone
  if (value === 'true' || value === 'on' || flagValue === true) {
    return true
  }

  const requirements = raw
    .split(/[,;]/)
    .map((r) => r.trim())
    .filter(Boolean)

  // 3. OR over all requirements
  return requirements.some((requirement) => {
    if (isDateTimeString(requirement)) {
      return isDateTimeReached(requirement) // global schedule, no user context needed
    }

    // fall back to existing tag-based logic
    if (!userTags) return false
    const userTagsArray = userTags
      .toLowerCase()
      .split(/[,;]/)
      .map((t) => t.trim())
    return userTagsArray.includes(requirement.toLowerCase())
  })
}

function parseDateTime(value: string): DateTime | null {
  const tryParse = (parser: () => DateTime): DateTime | null => {
    try {
      const parsed = parser()
      return parsed.isValid ? parsed : null
    } catch {
      return null
    }
  }

  return tryParse(() => DateTime.fromISO(value, { setZone: true })) ?? null
}

function isDateTimeString(value: string): boolean {
  return parseDateTime(value) !== null
}

function isDateTimeReached(iso: string): boolean {
  const target = parseDateTime(iso)
  if (!target) return false
  return DateTime.utc() >= target.toUTC()
}
